import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { FaArrowUp, FaArrowDown, FaEquals } from 'react-icons/fa6';

import PageHeader from '../../components/misc/PageHeader';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import * as bankAccountService from '../../../services/community/bankAccountService';
import { ProfitAndLossCategoryRow, ProfitAndLossDetailed } from '../../../types/bankAccount';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { ReportRange } from '../../components/financial/BookYearSelector';

interface FinancialProfitLossPageProps {
  hideHeader?: boolean;
  range: ReportRange | null;
  bookYearId?: number | null;
}

export default function FinancialProfitLossPage({
  hideHeader = false,
  range,
  bookYearId: legacyBookYearId = null,
}: FinancialProfitLossPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.reports.profitLoss' });
  const community = useSelectedCommunity();
  const [data, setData] = useState<ProfitAndLossDetailed | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Back-compat: callers passing bookYearId instead of range
  const effectiveRange: ReportRange | null =
    range ??
    (legacyBookYearId != null
      ? { mode: 'bookYear', bookYearId: legacyBookYearId }
      : null);

  useEffect(() => {
    if (!community || effectiveRange == null) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const promise =
      effectiveRange.mode === 'bookYear'
        ? bankAccountService.getProfitAndLossDetailed(effectiveRange.bookYearId)
        : bankAccountService.getProfitAndLossForDateRange(
            effectiveRange.fromDate,
            effectiveRange.toDate,
          );
    promise
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [community, effectiveRange]);

  const locale = i18n.language;

  if (loading && !data) {
    return <div className="p-8 text-sm text-primary-900">{t('loading')}</div>;
  }

  if (!data) {
    return (
      <div className="p-8 text-sm text-primary-900">
        {effectiveRange == null ? t('noBookYearSelected') : t('noData')}
      </div>
    );
  }

  const { bookYear, categories, totals } = data;
  const startLabel = bookYear
    ? dayjs(bookYear.startDate).format('D MMM YYYY')
    : effectiveRange?.mode === 'dateRange'
      ? dayjs(effectiveRange.fromDate).format('D MMM YYYY')
      : '';
  const endLabel = bookYear
    ? dayjs(bookYear.endDate).format('D MMM YYYY')
    : effectiveRange?.mode === 'dateRange'
      ? dayjs(effectiveRange.toDate).format('D MMM YYYY')
      : '';

  const incomeRows: ProfitAndLossCategoryRow[] = categories.filter(
    (c) => (c.income ?? 0) > 0 || (c.budgetedIncome ?? 0) > 0,
  );
  const expenseRows: ProfitAndLossCategoryRow[] = categories.filter(
    (c) => (c.expense ?? 0) > 0 || (c.budgetedExpense ?? 0) > 0,
  );

  const budgetedIncomeTotal = incomeRows.reduce((sum, c) => sum + (c.budgetedIncome ?? 0), 0);
  const budgetedExpenseTotal = expenseRows.reduce((sum, c) => sum + (c.budgetedExpense ?? 0), 0);
  const budgetedNet = budgetedIncomeTotal - budgetedExpenseTotal;
  const resultPositive = totals.result >= 0;

  const fmt = (n: number) => formatEuroCentsWithSign(n, locale);
  const dash = '—';

  return (
    <>
      {!hideHeader && <PageHeader title={t('title')} subtitle={t('subtitle')} />}

      <div className="flex flex-col gap-6">
        {/* Period header */}
        <div className="rounded-lg bg-secondary-100 px-4 py-2 text-sm text-primary-900">
          {t('period', {
            start: startLabel,
            end: endLabel,
            label: bookYear?.label ?? '',
          })}
        </div>

        <div className="rounded-xl border border-secondary-200 bg-white overflow-hidden">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col />
              <col className="w-[150px]" />
              <col className="w-[140px]" />
              <col className="w-[150px]" />
            </colgroup>

            {/* INCOME section */}
            <thead className="bg-success/5">
              <tr>
                <th colSpan={4} className="px-6 py-3 text-left">
                  <span className="inline-flex items-center gap-2 text-base font-semibold text-success">
                    <FaArrowUp />
                    {t('income.title')}
                  </span>
                </th>
              </tr>
              <tr className="border-y border-success/20">
                <th className="px-6 py-2 text-left text-xs font-semibold uppercase tracking-wide text-primary-900/50" />
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-primary-900/50">
                  {t('actual')}
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-primary-900/50">
                  {t('budgeted')}
                </th>
                <th className="px-6 py-2 text-right text-xs font-semibold uppercase tracking-wide text-primary-900/50">
                  {t('difference')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {incomeRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-3 text-sm text-primary-900/60">
                    {dash}
                  </td>
                </tr>
              ) : (
                incomeRows.map((row) => {
                  const diff = (row.income ?? 0) - (row.budgetedIncome ?? 0);
                  return (
                    <tr key={`income-${row.financialCategoryId}`}>
                      <td className="px-6 py-2">
                        <span className="flex items-center gap-2 text-primary-900/90">
                          <span
                            className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: row.color }}
                          />
                          {row.name}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-primary-900">
                        {(row.income ?? 0) > 0 ? fmt(row.income ?? 0) : dash}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-primary-900/80">
                        {(row.budgetedIncome ?? 0) > 0 ? fmt(row.budgetedIncome ?? 0) : dash}
                      </td>
                      <td
                        className={`whitespace-nowrap px-6 py-2 text-right tabular-nums ${
                          diff === 0
                            ? 'text-primary-900/60'
                            : diff > 0
                              ? 'text-success'
                              : 'text-danger'
                        }`}>
                        {(row.budgetedIncome ?? 0) > 0 || (row.income ?? 0) > 0 ? fmt(diff) : dash}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-success/30 bg-success/5">
                <td className="px-6 py-2 font-semibold text-primary-900">
                  {t('income.totalIncome')}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-base font-semibold tabular-nums text-success">
                  {fmt(totals.totalIncome)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm font-semibold tabular-nums text-primary-900/80">
                  {budgetedIncomeTotal > 0 ? fmt(budgetedIncomeTotal) : dash}
                </td>
                <td
                  className={`whitespace-nowrap px-6 py-2 text-right text-base font-semibold tabular-nums ${
                    totals.totalIncome - budgetedIncomeTotal === 0
                      ? 'text-primary-900/60'
                      : totals.totalIncome - budgetedIncomeTotal > 0
                        ? 'text-success'
                        : 'text-danger'
                  }`}>
                  {budgetedIncomeTotal > 0 ? fmt(totals.totalIncome - budgetedIncomeTotal) : dash}
                </td>
              </tr>
            </tfoot>

            {/* EXPENSES section */}
            <thead className="bg-danger/5">
              <tr>
                <th colSpan={4} className="px-6 py-3 text-left">
                  <span className="inline-flex items-center gap-2 text-base font-semibold text-danger">
                    <FaArrowDown />
                    {t('costs.title')}
                  </span>
                </th>
              </tr>
              <tr className="border-y border-danger/20">
                <th className="px-6 py-2 text-left text-xs font-semibold uppercase tracking-wide text-primary-900/50" />
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-primary-900/50">
                  {t('actual')}
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-primary-900/50">
                  {t('budgeted')}
                </th>
                <th className="px-6 py-2 text-right text-xs font-semibold uppercase tracking-wide text-primary-900/50">
                  {t('difference')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {expenseRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-3 text-sm text-primary-900/60">
                    {dash}
                  </td>
                </tr>
              ) : (
                expenseRows.map((row) => {
                  // For expenses we display as negative numbers. Difference = budgeted − actual
                  // so positive (green) when we spent LESS than budgeted.
                  const diff = -(row.expense ?? 0) - (-(row.budgetedExpense ?? 0));
                  return (
                    <tr key={`expense-${row.financialCategoryId}`}>
                      <td className="px-6 py-2">
                        <span className="flex items-center gap-2 text-primary-900/90">
                          <span
                            className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: row.color }}
                          />
                          {row.name}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-primary-900">
                        {(row.expense ?? 0) > 0 ? fmt(-(row.expense ?? 0)) : dash}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-primary-900/80">
                        {(row.budgetedExpense ?? 0) > 0 ? fmt(-(row.budgetedExpense ?? 0)) : dash}
                      </td>
                      <td
                        className={`whitespace-nowrap px-6 py-2 text-right tabular-nums ${
                          diff === 0
                            ? 'text-primary-900/60'
                            : diff > 0
                              ? 'text-success'
                              : 'text-danger'
                        }`}>
                        {(row.budgetedExpense ?? 0) > 0 || (row.expense ?? 0) > 0 ? fmt(diff) : dash}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-danger/30 bg-danger/5">
                <td className="px-6 py-2 font-semibold text-primary-900">
                  {t('costs.totalCosts')}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-base font-semibold tabular-nums text-danger">
                  {fmt(-totals.totalExpense)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm font-semibold tabular-nums text-primary-900/80">
                  {budgetedExpenseTotal > 0 ? fmt(-budgetedExpenseTotal) : dash}
                </td>
                <td
                  className={`whitespace-nowrap px-6 py-2 text-right text-base font-semibold tabular-nums ${
                    -totals.totalExpense - -budgetedExpenseTotal === 0
                      ? 'text-primary-900/60'
                      : -totals.totalExpense - -budgetedExpenseTotal > 0
                        ? 'text-success'
                        : 'text-danger'
                  }`}>
                  {budgetedExpenseTotal > 0 ? fmt(-totals.totalExpense - -budgetedExpenseTotal) : dash}
                </td>
              </tr>
            </tfoot>

            {/* NET RESULT */}
            <tfoot>
              <tr className="border-t-2 border-secondary-300 bg-secondary-50">
                <td className="px-6 py-3 font-bold text-primary-900">
                  <span className="inline-flex items-center gap-2">
                    <FaEquals />
                    {t('result.title')}
                  </span>
                </td>
                <td
                  className={`whitespace-nowrap px-3 py-3 text-right text-xl font-bold tabular-nums ${
                    resultPositive ? 'text-success' : 'text-danger'
                  }`}>
                  {fmt(totals.result)}
                </td>
                <td
                  className={`whitespace-nowrap px-3 py-3 text-right text-sm font-semibold tabular-nums ${
                    budgetedNet > 0
                      ? 'text-success'
                      : budgetedNet < 0
                        ? 'text-danger'
                        : 'text-primary-900/60'
                  }`}>
                  {budgetedNet !== 0 ? fmt(budgetedNet) : dash}
                </td>
                <td
                  className={`whitespace-nowrap px-6 py-3 text-right text-xl font-bold tabular-nums ${
                    totals.result - budgetedNet === 0
                      ? 'text-primary-900/60'
                      : totals.result - budgetedNet > 0
                        ? 'text-success'
                        : 'text-danger'
                  }`}>
                  {budgetedNet !== 0 ? fmt(totals.result - budgetedNet) : dash}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
}